import { isRequestedDestinationPath, isSafeAppPath, isSafeDestinationPath } from '@repo/common';
export { isSafeAppPath, isSafeDestinationPath };
export const PENDING_DESTINATION_STORAGE_KEY = 'starthub:pending_destination';
const DESTINATION_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
function parseStoredDestination(raw) {
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (isRequestedDestinationPath(parsed?.path) &&
            typeof parsed?.timestamp === 'number' &&
            Date.now() - parsed.timestamp < DESTINATION_MAX_AGE_MS) {
            return { path: parsed.path, timestamp: parsed.timestamp };
        }
        return null;
    }
    catch {
        return null;
    }
}
/**
 * Saves the intended member-app destination in tab-scoped sessionStorage and
 * cross-tab localStorage fallback so it survives login/signup, email verification
 * across new browser tabs, OAuth redirects, and onboarding steps.
 */
export function persistPendingDestination(path) {
    if (!isRequestedDestinationPath(path)) {
        return;
    }
    const payload = {
        path,
        timestamp: Date.now(),
    };
    const serialized = JSON.stringify(payload);
    try {
        sessionStorage.setItem(PENDING_DESTINATION_STORAGE_KEY, serialized);
    }
    catch {
        // sessionStorage not available (e.g. storage disabled or quota exceeded)
    }
    try {
        localStorage.setItem(PENDING_DESTINATION_STORAGE_KEY, serialized);
    }
    catch {
        // localStorage not available
    }
}
/**
 * Reads the current pending destination without clearing it.
 * Checks sessionStorage first, falling back to localStorage for cross-tab continuity.
 * The cross-tab copy keeps its original timestamp so reading never extends the intent's lifetime.
 */
export function readPendingDestination() {
    try {
        const sessionRaw = sessionStorage.getItem(PENDING_DESTINATION_STORAGE_KEY);
        const sessionPayload = parseStoredDestination(sessionRaw);
        if (sessionPayload) {
            return sessionPayload.path;
        }
        if (sessionRaw !== null) {
            sessionStorage.removeItem(PENDING_DESTINATION_STORAGE_KEY);
        }
    }
    catch {
        // sessionStorage not available
    }
    try {
        const localRaw = localStorage.getItem(PENDING_DESTINATION_STORAGE_KEY);
        const localPayload = parseStoredDestination(localRaw);
        if (localPayload) {
            try {
                sessionStorage.setItem(PENDING_DESTINATION_STORAGE_KEY, JSON.stringify(localPayload));
            }
            catch {
                // sessionStorage not available
            }
            return localPayload.path;
        }
        if (localRaw !== null) {
            localStorage.removeItem(PENDING_DESTINATION_STORAGE_KEY);
        }
    }
    catch {
        // localStorage not available
    }
    return null;
}
/**
 * Atomically consumes and removes the pending destination from both storage scopes.
 * Prevents stale intent from triggering on subsequent logins or sessions.
 */
export function consumePendingDestination() {
    const destination = readPendingDestination();
    clearPendingDestination();
    return destination;
}
/**
 * Explicitly clears any stored pending destination from both storage scopes.
 */
export function clearPendingDestination() {
    try {
        sessionStorage.removeItem(PENDING_DESTINATION_STORAGE_KEY);
    }
    catch {
        // ignore
    }
    try {
        localStorage.removeItem(PENDING_DESTINATION_STORAGE_KEY);
    }
    catch {
        // ignore
    }
}
/**
 * Resolves where an auth step continues: the explicit destination from the current
 * URL always wins, the stored intent is used only when the URL carries none.
 * Either way the stored intent is consumed so it cannot hijack a later login.
 */
export function consumeAuthDestination(explicitDestination) {
    const stored = consumePendingDestination();
    if (isRequestedDestinationPath(explicitDestination)) {
        return explicitDestination;
    }
    return stored;
}
/**
 * Login href that preserves the page being recovered.
 * `redirect` is a path+search only — never a full URL.
 */
export function buildLoginRedirectHref(loginPath, location) {
    if (location.pathname === loginPath) {
        return loginPath;
    }
    const requested = `${location.pathname}${location.search ?? ''}`;
    if (!isSafeAppPath(requested)) {
        return loginPath;
    }
    return `${loginPath}?redirect=${encodeURIComponent(requested)}`;
}
//# sourceMappingURL=build-login-redirect-href.js.map